"use client";

import React, { useState } from "react";
import Image from "next/image";
import { LuLayoutGrid } from "react-icons/lu";
import "./TechnologiesSection.css";

// Using devicon CDN for professional SVG logos
// Format: https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/{name}/{name}-{variant}.svg
const DI = "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons";

// Safe Tech Image component to handle load errors safely and fallback internally
const SafeTechImage = ({ src, alt, className, fallbackText }) => {
  const [hasError, setHasError] = useState(false);
  if (hasError || !src) {
    return <span className="tech-logo-fallback">{fallbackText}</span>;
  }
  return (
    <Image
      src={src}
      alt={alt}
      className={className}
      width={32}
      height={32}
      style={{ objectFit: "contain" }}
      onError={() => setHasError(true)}
    />
  );
};

const techData = [
  {
    id: "backend",
    label: "Backend",
    items: [
      { name: "Java", logo: `${DI}/java/java-original.svg` },
      { name: "Spring Boot", logo: `${DI}/spring/spring-original.svg` },
      { name: "Python", logo: `${DI}/python/python-original.svg` },
      { name: "Django", logo: `${DI}/django/django-plain.svg` },
      { name: "FastAPI", logo: `${DI}/fastapi/fastapi-original.svg` },
      { name: "Flask", logo: `${DI}/flask/flask-original.svg` },
      { name: "Node.js", logo: `${DI}/nodejs/nodejs-original.svg` },
      { name: "C# / .NET", logo: `${DI}/csharp/csharp-original.svg` },
      { name: "ASP.NET Core", logo: `${DI}/dotnetcore/dotnetcore-original.svg` },
      { name: "PHP", logo: `${DI}/php/php-original.svg` },
      { name: "Laravel", logo: `${DI}/laravel/laravel-original.svg` },
      { name: "Symfony", logo: `${DI}/symfony/symfony-original.svg` },
      { name: "Go", logo: `${DI}/go/go-original.svg` },
      { name: "Ruby on Rails", logo: `${DI}/rails/rails-original-wordmark.svg` },
      { name: "C++", logo: `${DI}/cplusplus/cplusplus-original.svg` },
      { name: "TensorFlow", logo: `${DI}/tensorflow/tensorflow-original.svg` },
      { name: "PyTorch", logo: `${DI}/pytorch/pytorch-original.svg` },
      { name: "Hibernate", logo: `${DI}/hibernate/hibernate-original.svg` },
      { name: "GraphQL", logo: `${DI}/graphql/graphql-plain.svg` },
      { name: "PostgreSQL", logo: `${DI}/postgresql/postgresql-original.svg` },
    ],
  },
  {
    id: "frontend",
    label: "Frontend",
    items: [
      { name: "JavaScript", logo: `${DI}/javascript/javascript-original.svg` },
      { name: "TypeScript", logo: `${DI}/typescript/typescript-original.svg` },
      { name: "React", logo: `${DI}/react/react-original.svg` },
      { name: "Next.js", logo: `${DI}/nextjs/nextjs-original.svg` },
      { name: "Vue.js", logo: `${DI}/vuejs/vuejs-original.svg` },
      { name: "Angular", logo: `${DI}/angularjs/angularjs-original.svg` },
      { name: "Redux", logo: `${DI}/redux/redux-original.svg` },
      { name: "Tailwind CSS", logo: `${DI}/tailwindcss/tailwindcss-original.svg` },
      { name: "HTML5", logo: `${DI}/html5/html5-original.svg` },
      { name: "CSS3", logo: `${DI}/css3/css3-original.svg` },
      { name: "Sass", logo: `${DI}/sass/sass-original.svg` },
      { name: "Webpack", logo: `${DI}/webpack/webpack-original.svg` },
    ],
  },
  {
    id: "mobile",
    label: "Mobile",
    items: [
      { name: "React Native", logo: `${DI}/react/react-original.svg` },
      { name: "Flutter", logo: `${DI}/flutter/flutter-original.svg` },
      { name: "Swift", logo: `${DI}/swift/swift-original.svg` },
      { name: "Kotlin", logo: `${DI}/kotlin/kotlin-original.svg` },
      { name: "Android", logo: `${DI}/android/android-original.svg` },
      { name: "Xcode", logo: `${DI}/xcode/xcode-original.svg` },
    ],
  },
  {
    id: "devops",
    label: "DevOps",
    items: [
      { name: "Docker", logo: `${DI}/docker/docker-original.svg` },
      { name: "Kubernetes", logo: `${DI}/kubernetes/kubernetes-original.svg` },
      { name: "Terraform", logo: `${DI}/terraform/terraform-original.svg` },
      { name: "Ansible", logo: `${DI}/ansible/ansible-original.svg` },
      { name: "Jenkins", logo: `${DI}/jenkins/jenkins-original.svg` },
      { name: "GitHub Actions", logo: `${DI}/github/github-original.svg` },
      { name: "AWS", logo: `${DI}/amazonwebservices/amazonwebservices-original-wordmark.svg` },
      { name: "GCP", logo: `${DI}/googlecloud/googlecloud-original.svg` },
      { name: "Azure", logo: `${DI}/azure/azure-original.svg` },
      { name: "Linux", logo: `${DI}/linux/linux-original.svg` },
    ],
  },
  {
    id: "database",
    label: "Databases",
    items: [
      { name: "PostgreSQL", logo: `${DI}/postgresql/postgresql-original.svg` },
      { name: "MongoDB", logo: `${DI}/mongodb/mongodb-original.svg` },
      { name: "MySQL", logo: `${DI}/mysql/mysql-original.svg` },
      { name: "Redis", logo: `${DI}/redis/redis-original.svg` },
      { name: "Oracle", logo: `${DI}/oracle/oracle-original.svg` },
      { name: "MS SQL", logo: `${DI}/microsoftsqlserver/microsoftsqlserver-original.svg` },
      { name: "Elasticsearch", logo: `${DI}/elasticsearch/elasticsearch-original.svg` },
      { name: "Firebase", logo: `${DI}/firebase/firebase-original.svg` },
      { name: "Supabase", logo: `${DI}/supabase/supabase-original.svg` },
      { name: "SQLite", logo: `${DI}/sqlite/sqlite-original.svg` },
    ],
  },
  {
    id: "qa",
    label: "QA & Testing",
    items: [
      { name: "Selenium", logo: `${DI}/selenium/selenium-original.svg` },
      { name: "Postman", logo: `${DI}/postman/postman-original.svg` },
      { name: "Jest", logo: `${DI}/jest/jest-plain.svg` },
      { name: "Cypress", logo: `${DI}/cypressio/cypressio-original.svg` },
      { name: "Playwright", logo: `${DI}/playwright/playwright-original.svg` },
      { name: "Mocha", logo: `${DI}/mocha/mocha-original.svg` },
    ],
  },
  {
    id: "ai",
    label: "AI / ML",
    items: [
      { name: "TensorFlow", logo: `${DI}/tensorflow/tensorflow-original.svg` },
      { name: "PyTorch", logo: `${DI}/pytorch/pytorch-original.svg` },
      { name: "Keras", logo: `${DI}/keras/keras-original.svg` },
      { name: "NumPy", logo: `${DI}/numpy/numpy-original.svg` },
      { name: "Pandas", logo: `${DI}/pandas/pandas-original.svg` },
      { name: "OpenCV", logo: `${DI}/opencv/opencv-original.svg` },
      { name: "Jupyter", logo: `${DI}/jupyter/jupyter-original.svg` },
      { name: "Python", logo: `${DI}/python/python-original.svg` },
    ],
  },
  {
    id: "tools",
    label: "Tools & Others",
    items: [
      { name: "Git", logo: `${DI}/git/git-original.svg` },
      { name: "GitHub", logo: `${DI}/github/github-original.svg` },
      { name: "VS Code", logo: `${DI}/vscode/vscode-original.svg` },
      { name: "Figma", logo: `${DI}/figma/figma-original.svg` },
      { name: "Jira", logo: `${DI}/jira/jira-original.svg` },
      { name: "Nginx", logo: `${DI}/nginx/nginx-original.svg` },
      { name: "GraphQL", logo: `${DI}/graphql/graphql-plain.svg` },
      { name: "REST API", logo: `${DI}/nodejs/nodejs-original.svg` },
      { name: "Bash", logo: `${DI}/bash/bash-original.svg` },
      { name: "Markdown", logo: `${DI}/markdown/markdown-original.svg` },
    ],
  },
];

const DI_URL = "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons";

const TECH_PRESETS = {
  "java": `${DI_URL}/java/java-original.svg`,
  "spring": `${DI_URL}/spring/spring-original.svg`,
  "spring boot": `${DI_URL}/spring/spring-original.svg`,
  "python": `${DI_URL}/python/python-original.svg`,
  "django": `${DI_URL}/django/django-plain.svg`,
  "flask": `${DI_URL}/flask/flask-original.svg`,
  "fastapi": `${DI_URL}/fastapi/fastapi-original.svg`,
  "c#": `${DI_URL}/csharp/csharp-original.svg`,
  "asp.net core": `${DI_URL}/dotnetcore/dotnetcore-original.svg`,
  "php": `${DI_URL}/php/php-original.svg`,
  "laravel": `${DI_URL}/laravel/laravel-original.svg`,
  "symfony": `${DI_URL}/symfony/symfony-original.svg`,
  "go": `${DI_URL}/go/go-original.svg`,
  "ruby on rails": `${DI_URL}/rails/rails-original-wordmark.svg`,
  "c++": `${DI_URL}/cplusplus/cplusplus-original.svg`,
  "ue4": `${DI_URL}/unrealengine/unrealengine-original.svg`,
  "tensorflow": `${DI_URL}/tensorflow/tensorflow-original.svg`,
  "pytorch": `${DI_URL}/pytorch/pytorch-original.svg`,
  "postgresql": `${DI_URL}/postgresql/postgresql-original.svg`,
  "oracle": `${DI_URL}/oracle/oracle-original.svg`,
  "ms sql": `${DI_URL}/microsoftsqlserver/microsoftsqlserver-original.svg`,
  "mongodb": `${DI_URL}/mongodb/mongodb-original.svg`,
  "redis": `${DI_URL}/redis/redis-original.svg`,
  "elasticsearch": `${DI_URL}/elasticsearch/elasticsearch-original.svg`,
  "javascript": `${DI_URL}/javascript/javascript-original.svg`,
  "typescript": `${DI_URL}/typescript/typescript-original.svg`,
  "react": `${DI_URL}/react/react-original.svg`,
  "redux": `${DI_URL}/redux/redux-original.svg`,
  "vue.js": `${DI_URL}/vuejs/vuejs-original.svg`,
  "angular": `${DI_URL}/angularjs/angularjs-original.svg`,
  "next.js": `${DI_URL}/nextjs/nextjs-original.svg`,
  "swift": `${DI_URL}/swift/swift-original.svg`,
  "android": `${DI_URL}/android/android-original.svg`,
  "kotlin": `${DI_URL}/kotlin/kotlin-original.svg`,
  "flutter": `${DI_URL}/flutter/flutter-original.svg`,
  "react native": `${DI_URL}/react/react-original.svg`,
  "kubernetes": `${DI_URL}/kubernetes/kubernetes-original.svg`,
  "terraform": `${DI_URL}/terraform/terraform-original.svg`,
  "ansible": `${DI_URL}/ansible/ansible-original.svg`,
  "jenkins": `${DI_URL}/jenkins/jenkins-original.svg`,
  "docker": `${DI_URL}/docker/docker-original.svg`,
  "selenium": `${DI_URL}/selenium/selenium-original.svg`,
  "postman": `${DI_URL}/postman/postman-original.svg`,
  "graphql": `${DI_URL}/graphql/graphql-plain.svg`,
  "git": `${DI_URL}/git/git-original.svg`,
  "github": `${DI_URL}/github/github-original.svg`,
  "vs code": `${DI_URL}/vscode/vscode-original.svg`,
  "figma": `${DI_URL}/figma/figma-original.svg`,
  "nginx": `${DI_URL}/nginx/nginx-original.svg`
};

const getImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;

  const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";

  if (url.includes("/uploads/")) {
    const cleanPath = url.substring(url.indexOf("/uploads/"));
    return `${apiUrl}${cleanPath}`;
  }

  if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `${apiUrl}${cleanPath}`;
  }

  if (url.startsWith("http")) return url;
  return url;
};

const renderTechIcon = (iconName, techName = "", size = 32) => {
  if (!iconName) return null;
  const formattedName = iconName.startsWith("Lu") ? iconName : `Lu${iconName}`;
  const IconComponent = LuIcons[formattedName] || LuIcons.LuLayoutGrid;
  return <IconComponent size={size} style={{ color: "#f97316" }} />;
};

export default function TechnologiesSection({ title, description, categories }) {
  const [activeCategory, setActiveCategory] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const sectionRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const isScrollingRef = React.useRef(false);

  const displayTechData = (categories && categories.length > 0)
    ? categories.map((cat, idx) => ({
      id: cat.category.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      label: cat.category,
      items: cat.technologies.map(t => {
        const cleanName = t.name.toLowerCase().trim().replace(/\.js$/, "js").replace(/\s+/g, "");
        return {
          name: t.name,
          icon: t.icon,
          logo: `${DI}/${cleanName}/${cleanName}-original.svg`
        };
      })
    }))
    : techData;

  const currentActiveCategory = activeCategory || (displayTechData[0]?.id || "backend");
  const activeData = displayTechData.find((cat) => cat.id === currentActiveCategory);

  const activeCategoryRef = React.useRef(activeCategory);
  const displayTechDataRef = React.useRef(displayTechData);

  React.useEffect(() => {
    activeCategoryRef.current = activeCategory;
  }, [activeCategory]);

  React.useEffect(() => {
    displayTechDataRef.current = displayTechData;
  }, [displayTechData]);

  const handleNavClick = (id) => {
    setActiveCategory(id);
  };

  return (
    <div className="tech-sticky-wrapper" ref={wrapperRef}>
      <section className="tech-section" ref={sectionRef}>
        <div className="tech-container">
          {/* Header */}
          <div className="tech-header" data-aos="fade-up">
            <span className="tech-eyebrow">TECHNOLOGY</span>
            <h2 className="tech-main-title">
              {title || "Technologies We Support"}
            </h2>
            <p className="tech-description">
              {description || "Our experts are proficient in a wide range of technologies, ensuring that you have the right skills for any project. Here's a snapshot of the technologies we support:"}
            </p>
          </div>

          {/* Main body: sidebar + grid */}
          <div className="tech-body">
            {/* Mobile-only Custom Dropdown */}
            <div className="tech-mobile-dropdown-container">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="tech-mobile-select-btn"
              >
                <span>{activeData?.label}</span>
                <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
              </button>

              {isOpen && (
                <ul className="tech-mobile-dropdown-list">
                  {displayTechData.map((cat) => (
                    <li
                      key={cat.id}
                      className={`tech-mobile-dropdown-item ${currentActiveCategory === cat.id ? "active" : ""}`}
                      onClick={() => {
                        handleNavClick(cat.id);
                        setIsOpen(false);
                      }}
                    >
                      {cat.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Left sidebar */}
            <nav className="tech-sidebar" data-aos="fade-right">
              {displayTechData.map((cat) => (
                <button
                  key={cat.id}
                  className={`tech-nav-btn ${currentActiveCategory === cat.id ? "active" : ""}`}
                  onClick={() => handleNavClick(cat.id)}
                >
                  {currentActiveCategory === cat.id && <span className="nav-active-bar" />}
                  {cat.label}
                </button>
              ))}
            </nav>

            {/* Right: logo grid */}
            <div className="tech-grid-panel" data-aos="fade-left">
              <div className="tech-grid">
                {activeData?.items.map((item, idx) => (
                  <div
                    key={`${currentActiveCategory}-${idx}`}
                    className="tech-item-card"
                    style={{ animationDelay: `${idx * 20}ms` }}
                  >
                    <div className="tech-item-icon">
                      {item.icon ? (
                        renderTechIcon(item.icon, item.name)
                      ) : (
                        <SafeTechImage
                          src={item.logo}
                          alt={item.name}
                          className="tech-logo-img"
                          fallbackText={item.name.slice(0, 2).toUpperCase()}
                        />
                      )}
                    </div>
                    <span className="tech-item-name">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
